import React, { RefObject, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import DemoRollingNumber, { DemoRollingDirection } from './rolling-number'

import './calculated-fields-loader.scss'

const LOADING_ROOT_CLASS = 'demo-calculated-field-loading'

type LoadingField = {
  input: HTMLInputElement
  previousCaretColor: string
  previousColor: string
  previousTextFillColor: string
  root: HTMLElement
  value: string
}

type Props = {
  active: boolean
  containerRef: RefObject<HTMLElement>
  direction: DemoRollingDirection
  excludedFieldNames: string[]
  fieldValues: Record<string, unknown>
}

const isPopulatedNumericValue = (value: string) => {
  const normalizedValue = value.replace(/\s/g, '').replace(',', '.')

  return normalizedValue !== '' && Number.isFinite(Number(normalizedValue))
}

const getFieldValue = (input: HTMLInputElement, fieldValues: Record<string, unknown>) => {
  const fieldName = input.dataset.demoEditable || input.name

  if (fieldName && fieldName in fieldValues) {
    return String(fieldValues[fieldName] ?? '')
  }

  return input.value
}

export const getDemoCalculatedFieldValues = (container: HTMLElement | null) => {
  if (!container) {
    return {}
  }

  return Array.from(container.querySelectorAll<HTMLInputElement>('input'))
    .reduce<Record<string, string>>((values, input) => {
      const fieldName = input.dataset.demoEditable || input.name

      if (fieldName) {
        values[fieldName] = input.value
      }

      return values
    }, {})
}

const DemoCalculatedFieldsLoader = ({
  active,
  containerRef,
  direction,
  excludedFieldNames,
  fieldValues,
}: Props) => {
  const [ loadingFields, setLoadingFields ] = useState<LoadingField[]>([])

  useLayoutEffect(() => {
    if (!active || !containerRef.current) {
      setLoadingFields([])
      return
    }

    const fields = Array.from(containerRef.current.querySelectorAll<HTMLInputElement>('input'))
      .filter((input) => {
        const fieldName = input.dataset.demoEditable || input.name
        const fieldValue = getFieldValue(input, fieldValues)

        return !excludedFieldNames.includes(fieldName) && isPopulatedNumericValue(fieldValue)
      })
      .reduce<LoadingField[]>((result, input) => {
        const root = input.closest<HTMLElement>('.MuiOutlinedInput-root')

        if (root) {
          const previousCaretColor = input.style.caretColor
          const previousColor = input.style.color
          const previousTextFillColor = input.style.getPropertyValue('-webkit-text-fill-color')

          root.classList.add(LOADING_ROOT_CLASS)
          input.style.caretColor = 'transparent'
          input.style.color = 'transparent'
          input.style.setProperty('-webkit-text-fill-color', 'transparent')
          result.push({
            input,
            previousCaretColor,
            previousColor,
            previousTextFillColor,
            root,
            value: getFieldValue(input, fieldValues),
          })
        }

        return result
      }, [])

    setLoadingFields(fields)

    return () => {
      fields.forEach(({
        input,
        previousCaretColor,
        previousColor,
        previousTextFillColor,
        root,
      }) => {
        root.classList.remove(LOADING_ROOT_CLASS)
        input.style.caretColor = previousCaretColor
        input.style.color = previousColor

        if (previousTextFillColor) {
          input.style.setProperty('-webkit-text-fill-color', previousTextFillColor)
        } else {
          input.style.removeProperty('-webkit-text-fill-color')
        }
      })
    }
  }, [ active, containerRef, excludedFieldNames, fieldValues ])

  return <>
    {loadingFields.map(({ input, root, value }, index) => createPortal(
      <span className='demo-calculated-field-loading__overlay'>
        <DemoRollingNumber direction={direction} value={value} />
      </span>,
      root,
      `${input.name || value}-${index}`,
    ))}
  </>
}

export default DemoCalculatedFieldsLoader

